import React, { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { adminAPI } from '../services/api';

const formatUpdatedAt = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
};

const MagnusDocumentDialog = ({ userId, userName, open, onOpenChange }) => {
  const [content, setContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    const load = async () => {
      setLoading(true);
      try {
        const response = await adminAPI.getMagnusDocument(userId);
        setContent(response.data?.content || '');
        setUpdatedAt(response.data?.updated_at || null);
      } catch (error) {
        toast.error('Não foi possível carregar o documento.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, userId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await adminAPI.updateMagnusDocument(userId, content);
      setUpdatedAt(response.data?.updated_at || null);
      toast.success('Documento atualizado. A IA vai usar essa versão a partir da próxima dica.');
    } catch (error) {
      toast.error('Não foi possível salvar o documento.');
    } finally {
      setSaving(false);
    }
  };

  const updatedLabel = formatUpdatedAt(updatedAt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-white/10 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">Documento Meta Magnus — {userName}</DialogTitle>
          <DialogDescription className="text-slate-300">
            Contexto que a IA usa pra gerar as dicas de segunda, quarta e sexta. Sem documento, a IA gera dicas genéricas
            pra este membro nunca ficar sem orientação.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : (
          <>
            <Textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              rows={10}
              maxLength={20000}
              placeholder="Ex.: contexto sobre a Meta Magnus do membro, o que já foi tentado, obstáculos, pontos de atenção do mentor..."
              className="bg-neutral-900/50 border-neutral-800 text-white text-sm"
            />
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{content.length}/20000</span>
              <span>{updatedLabel ? `Última atualização: ${updatedLabel}` : 'Nenhum documento anexado ainda'}</span>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" className="border-neutral-700 text-white" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={handleSave} disabled={loading || saving}>
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminMagnusPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await adminAPI.getMagnusProgress();
        setRows(response.data?.rows || []);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-magnus-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Magnus</h1>
          <p className="text-slate-400 mt-1">
            Comparativo de progresso da Meta Magnus por mentorado (baseado apenas no NPS) e o documento de contexto que
            orienta as dicas automáticas de segunda, quarta e sexta.
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/20 p-4 md:p-6">
          {loading ? (
            <p className="text-slate-300">Carregando...</p>
          ) : rows.length === 0 ? (
            <p className="text-slate-300">Nenhum mentorado encontrado.</p>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-4">
              {rows.map((item) => (
                <div
                  key={item.user_id}
                  className="grid grid-cols-[120px_1fr_auto_auto] md:grid-cols-[220px_1fr_auto_auto] gap-3 md:gap-5 items-center"
                >
                  <p className="text-white font-medium truncate">{item.full_name}</p>
                  <div className="h-7 rounded-full border-2 border-white/30 overflow-hidden bg-white/5">
                    <div
                      className="h-full bg-indigo-400/90 transition-all duration-500"
                      style={{ width: `${Math.max(0, Math.min(item.progress, 100))}%` }}
                    />
                  </div>
                  <p className="text-white font-semibold min-w-[56px] text-right">{item.progress}%</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-slate-300 hover:text-white"
                    onClick={() => setSelectedMember(item)}
                  >
                    <FileText size={14} className="mr-1" />
                    Documento
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MagnusDocumentDialog
        userId={selectedMember?.user_id}
        userName={selectedMember?.full_name}
        open={!!selectedMember}
        onOpenChange={(open) => !open && setSelectedMember(null)}
      />
    </Layout>
  );
}
